// Global Instructions Rule Applied!
//
// Configuration → GL Accounts: the GLOBAL accounting settings every fund
// inherits.
//
//   1. The GL account catalogue (cfg.acc_gl_accounts) with usage figures, so
//      the effect of a change is visible before making it.
//   2. The global accounting-item -> account default mapping
//      (cfg.acc_gl_account_items). Moving one is PROSPECTIVE ONLY: no journal
//      moves, and funds with their own per-fund override are unaffected. Only
//      funds that follow the default are touched, and only from now on -
//      which is why the confirm dialog names them.
//
// Retrospective, per-fund moves are a different operation with different
// accounting consequences: they live on the fund's Chart of Accounts tab.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Input, Modal, Select, Table, Tag, Typography } from 'antd'
import { PlusOutlined, EditOutlined, ReloadOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import type {
  GlAccountAdminRow,
  GlItemDefaultRow
} from '../../../state_manager/data_models/fund_chart_of_accounts.ts'
import {
  fetchGlAccountAdmin,
  saveGlAccount,
  setGlobalItemMapping
} from '../services/accountingConfigService.ts'
import { CONFIG_PANEL_SCROLL_CLASS } from '../configModules.ts'
import ExportRowsButton from '../../shared/table_grid_export/ExportRowsButton.tsx'

const { Title, Text } = Typography

export default function GLAccountsPanel() {
  const [accounts, setAccounts] = useState<GlAccountAdminRow[]>([])
  const [accountTypes, setAccountTypes] = useState<Array<{ value: string; label: string }>>([])
  const [items, setItems] = useState<GlItemDefaultRow[]>([])
  const [loading, setLoading] = useState(false)

  // add / edit account modal
  const [accountModalOpen, setAccountModalOpen] = useState(false)
  const [editing, setEditing] = useState<GlAccountAdminRow | null>(null)
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [typeId, setTypeId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // mapping-move confirmation
  const [mappingChange, setMappingChange] = useState<{ item: GlItemDefaultRow; glAccountId: string } | null>(null)
  const [movingMapping, setMovingMapping] = useState(false)

  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchGlAccountAdmin(token())
      if (res.error) {
        showErrorToast(res.error)
        return
      }
      setAccounts(res.accounts)
      setAccountTypes(res.accountTypes)
      setItems(res.items)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const accountOptions = useMemo(
    () =>
      accounts.map((a) => ({
        value: a.cfg_acc_gl_accounts__id,
        label: `${a.acc_gl_accounts__account_code ? `${a.acc_gl_accounts__account_code} · ` : ''}${a.acc_gl_accounts__account_name}`
      })),
    [accounts]
  )

  const accountLabel = useCallback(
    (id: string | null | undefined) => accountOptions.find((o) => o.value === id)?.label ?? '—',
    [accountOptions]
  )

  const openCreate = useCallback(() => {
    setEditing(null)
    setCode('')
    setName('')
    setTypeId(null)
    setAccountModalOpen(true)
  }, [])

  const openEdit = useCallback((row: GlAccountAdminRow) => {
    setEditing(row)
    setCode(row.acc_gl_accounts__account_code ?? '')
    setName(row.acc_gl_accounts__account_name)
    setTypeId(row.cfg_acc_account_types__id ?? null)
    setAccountModalOpen(true)
  }, [])

  const submitAccount = useCallback(async () => {
    if (!name.trim() || !typeId) {
      showErrorToast('Account name and type are required')
      return
    }
    setSubmitting(true)
    try {
      const res = await saveGlAccount(
        {
          ...(editing ? { id: editing.cfg_acc_gl_accounts__id } : {}),
          account_code: code.trim() || null,
          account_name: name.trim(),
          account_type: typeId
        },
        token()
      )
      if (!res.success) {
        // duplicate code / name comes back as 422 - keep the modal open so the
        // value can be corrected rather than retyped
        showErrorToast(res.error || 'Failed to save the account')
        return
      }
      toast.success(res.data?.status_message || 'Account saved')
      setAccountModalOpen(false)
      await load()
    } finally {
      setSubmitting(false)
    }
  }, [editing, code, name, typeId, load])

  const submitMappingMove = useCallback(async () => {
    if (!mappingChange) return
    setMovingMapping(true)
    try {
      const res = await setGlobalItemMapping(
        mappingChange.item.cfg_acc_items__id,
        mappingChange.glAccountId,
        token()
      )
      if (!res.success) {
        showErrorToast(res.error || 'Failed to set the default mapping')
        return
      }
      toast.success(res.data?.status_message || 'Default mapping updated')
      setMappingChange(null)
      await load()
    } finally {
      setMovingMapping(false)
    }
  }, [mappingChange, load])

  const accountColumns: ColumnsType<GlAccountAdminRow> = [
    { title: 'Code', dataIndex: 'acc_gl_accounts__account_code', width: 90, render: (v) => v ?? '—' },
    { title: 'Account', dataIndex: 'acc_gl_accounts__account_name' },
    { title: 'Type', dataIndex: 'cfg_acc_account_types__label', width: 120, render: (v) => v ?? '—' },
    {
      title: 'Used by',
      dataIndex: 'prog__fund_count',
      width: 100,
      align: 'right',
      render: (v: number) => `${v} fund${v === 1 ? '' : 's'}`
    },
    { title: 'Journals', dataIndex: 'prog__journal_count', width: 100, align: 'right' },
    { title: 'Templates', dataIndex: 'prog__template_count', width: 100, align: 'right' },
    {
      title: '',
      width: 60,
      align: 'center',
      render: (_: unknown, row) => (
        <Button type='text' size='small' icon={<EditOutlined />} onClick={() => openEdit(row)} />
      )
    }
  ]

  const itemColumns: ColumnsType<GlItemDefaultRow> = [
    {
      title: 'Accounting item',
      dataIndex: 'cfg_acc_items__label',
      render: (v: string, row) => (
        <span>
          {v}
          {row.cfg_acc_items__call_component ? <Tag className='ml-2'>call component</Tag> : null}
        </span>
      )
    },
    {
      title: 'Default GL account',
      dataIndex: 'prog__default_gl_account_id',
      width: 320,
      render: (_: unknown, row) => (
        <Select
          size='small'
          className='w-full'
          showSearch
          optionFilterProp='label'
          placeholder='Select account...'
          value={row.prog__default_gl_account_id ?? undefined}
          options={accountOptions}
          onChange={(v) => setMappingChange({ item: row, glAccountId: v })}
        />
      )
    },
    {
      title: 'Overridden in',
      dataIndex: 'prog__override_fund_count',
      width: 130,
      align: 'right',
      render: (v: number) =>
        v > 0 ? <Text>{`${v} fund${v === 1 ? '' : 's'}`}</Text> : <Text type='secondary'>—</Text>
    }
  ]

  return (
    <div className={CONFIG_PANEL_SCROLL_CLASS}>
      <div className='flex items-center justify-between mb-3'>
        <Title level={5} style={{ margin: 0 }}>
          GL Accounts
        </Title>
        <div className='flex items-center gap-2'>
          <ExportRowsButton
            name='GL Accounts'
            rows={accounts}
            columns={[
              { title: 'Code', value: (r) => r.acc_gl_accounts__account_code },
              { title: 'Account', value: (r) => r.acc_gl_accounts__account_name },
              { title: 'Type', value: (r) => r.cfg_acc_account_types__label },
              // the grid shows 'n funds'; the export keeps the count
              { title: 'Used by', value: (r) => r.prog__fund_count },
              { title: 'Journals', value: (r) => r.prog__journal_count },
              { title: 'Templates', value: (r) => r.prog__template_count }
            ]}
          />
          <Button type='primary' size='small' icon={<PlusOutlined />} onClick={openCreate}>
            New Account
          </Button>
          <Button size='small' icon={<ReloadOutlined />} onClick={() => void load()}>
            Refresh
          </Button>
        </div>
      </div>

      <Table
        size='small'
        loading={loading}
        columns={accountColumns}
        dataSource={accounts}
        pagination={false}
        rowKey='cfg_acc_gl_accounts__id'
      />

      <Title level={5} className='mt-6'>
        Default item mapping
      </Title>
      <Text type='secondary' className='block mb-2'>
        Which GL account each accounting item books to, for every fund that has not set its own.
        Changing a default applies from now on — it never moves journals that have already been
        booked. To move an individual fund and its history, use that fund&apos;s Chart of Accounts tab.
      </Text>
      <Table
        size='small'
        loading={loading}
        columns={itemColumns}
        dataSource={items}
        pagination={false}
        rowKey='cfg_acc_items__id'
      />

      <Modal
        open={accountModalOpen}
        title={editing ? 'Edit GL account' : 'New GL account'}
        okText='Save'
        confirmLoading={submitting}
        onOk={() => void submitAccount()}
        onCancel={() => setAccountModalOpen(false)}
        destroyOnClose
      >
        <div className='flex flex-col gap-3'>
          <div>
            <Text type='secondary'>Code</Text>
            <Input value={code} maxLength={20} placeholder='e.g. 1010' onChange={(e) => setCode(e.target.value)} />
          </div>
          <div>
            <Text type='secondary'>Name</Text>
            <Input value={name} maxLength={120} placeholder='e.g. Cash' onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Text type='secondary'>Type</Text>
            <Select
              className='w-full'
              placeholder='Select type...'
              value={typeId}
              options={accountTypes}
              onChange={(v) => setTypeId(v)}
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={mappingChange !== null}
        title={mappingChange ? `Change the default account for “${mappingChange.item.cfg_acc_items__label}”?` : ''}
        okText='Change default'
        okButtonProps={{ danger: true }}
        confirmLoading={movingMapping}
        onOk={() => void submitMappingMove()}
        onCancel={() => setMappingChange(null)}
        destroyOnClose
      >
        {mappingChange ? (
          <div className='flex flex-col gap-3'>
            <div>
              <div>
                <Text type='secondary'>From:</Text>{' '}
                {accountLabel(mappingChange.item.prog__default_gl_account_id)}
              </div>
              <div>
                <Text type='secondary'>To:</Text> {accountLabel(mappingChange.glAccountId)}
              </div>
            </div>

            <div>
              <Text strong>
                {mappingChange.item.prog__affected_funds.length} fund
                {mappingChange.item.prog__affected_funds.length === 1 ? '' : 's'} without an override
                {mappingChange.item.prog__affected_funds.length > 0 ? ':' : '.'}
              </Text>
              {mappingChange.item.prog__affected_funds.length > 0 ? (
                <div className='mt-1'>
                  <Text type='secondary'>
                    {mappingChange.item.prog__affected_funds
                      .map((f) => f.funds__name ?? f.funds__id)
                      .join(', ')}
                  </Text>
                </div>
              ) : null}
              {mappingChange.item.prog__override_fund_count > 0 ? (
                <div className='mt-2'>
                  <Text type='secondary'>
                    {mappingChange.item.prog__override_fund_count} fund
                    {mappingChange.item.prog__override_fund_count === 1 ? '' : 's'} with their own
                    mapping are unaffected.
                  </Text>
                </div>
              ) : null}
            </div>

            <Text type='secondary'>
              Nothing already booked moves. The new account applies the next time this item is
              booked.
            </Text>
          </div>
        ) : null}
      </Modal>
    </div>
  )
}
